import { getCurrentSession } from './session';

/**
 * Logs the user out when they come back after closing every tab of the app (while the browser stayed
 * open; closing the browser already drops the session cookie).
 *
 * Browsers have no "last tab closed" event — `pagehide` fires alike for a close, a reload and leaving the
 * site — so the decision is made on the next page load instead:
 * - every tab with a session stamps the time into localStorage as it goes away;
 * - a page that loads to find a stamp asks the other tabs, over a BroadcastChannel, whether any is still
 *   open. If none answers and the stamp is older than a reload could explain, every tab had closed.
 *
 * The stamp is a timestamp, nothing that can authenticate anyone.
 */

const CHANNEL_NAME = 'uwe-tabs';
const CLOSED_AT_KEY = 'auth:tabClosedAt';
/** A page loading this soon after a tab went away is a reload (or a quick reopen), not a return. */
export const REOPEN_GRACE_MS = 30_000;
/** How long to wait for other tabs to answer the roll call. */
export const ROLL_CALL_WAIT_MS = 300;

type PresenceMessage = { type: 'roll-call' } | { type: 'here' };

const channel: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

// Answer every roll call while this tab is open. A tab never receives its own posts.
channel?.addEventListener('message', (message: MessageEvent<PresenceMessage>) => {
  if (message.data?.type === 'roll-call') channel.postMessage({ type: 'here' } satisfies PresenceMessage);
});

function readClosedAt(): number | null {
  try {
    const raw = localStorage.getItem(CLOSED_AT_KEY);
    return raw == null ? null : Number(raw);
  } catch {
    return null;
  }
}

function writeClosedAt(value: number | null) {
  try {
    if (value == null) localStorage.removeItem(CLOSED_AT_KEY);
    else localStorage.setItem(CLOSED_AT_KEY, String(value));
  } catch {
    // Private mode or blocked storage: closing every tab just won't log out.
  }
}

/** Whether any other tab of the app answers within ROLL_CALL_WAIT_MS. */
function anotherTabIsOpen(): Promise<boolean> {
  if (!channel) return Promise.resolve(false);
  return new Promise(resolve => {
    const onMessage = (message: MessageEvent<PresenceMessage>) => {
      if (message.data?.type !== 'here') return;
      finish(true);
    };
    const timer = setTimeout(() => finish(false), ROLL_CALL_WAIT_MS);
    function finish(open: boolean) {
      clearTimeout(timer);
      channel!.removeEventListener('message', onMessage);
      resolve(open);
    }
    channel.addEventListener('message', onMessage);
    channel.postMessage({ type: 'roll-call' } satisfies PresenceMessage);
  });
}

/**
 * Call once on page load, before resuming the session: true when the user is coming back after every tab
 * of the app was closed, and the session should be ended. Consumes the stamp either way.
 */
export async function allTabsWereClosed(): Promise<boolean> {
  const closedAt = readClosedAt();
  if (closedAt == null) return false;

  const closedLongAgo = Date.now() - closedAt >= REOPEN_GRACE_MS;
  const result = closedLongAgo && !(await anotherTabIsOpen());
  writeClosedAt(null);
  return result;
}

/** Starts stamping this tab's departure. Returns a function that stops it. */
export function startTabPresence(): () => void {
  const onPageHide = () => {
    if (getCurrentSession()) writeClosedAt(Date.now());
  };
  // Back from the back/forward cache: the tab never closed after all.
  const onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) writeClosedAt(null);
  };
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('pageshow', onPageShow);
  return () => {
    window.removeEventListener('pagehide', onPageHide);
    window.removeEventListener('pageshow', onPageShow);
  };
}
